import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AudioLines, ArrowRight, Sparkles } from "lucide-react";
import { ShowreelLauncher } from "@/components/showreel/ShowreelLauncher";
import { HologramHoverReveal } from "@/components/fx/HologramHoverReveal";
import { TID } from "@/lib/testIds";

const QUICK_PROMPTS = [
    "Which roles carry the highest SoD risk?",
    "Draft a BPD for Z_FI_AP_CLERK",
    "What does T-code FB60 authorize?",
];

/** Cinematic command-centre hero: cosmic canvas, the architect's portrait ringed
 * by an orbital halo, live counters, and a Command Center prompt that hands off
 * straight to the Utsav agent. */
export function GenesisHero({ stats, roleStats }) {
    const nav = useNavigate();
    const [prompt, setPrompt] = useState("");

    const ask = (text) => {
        const q = (text || prompt).trim();
        if (!q) return;
        nav("/utsav", { state: { prompt: q } });
    };

    const counters = [
        { k: "Workspaces", v: stats?.workspaces ?? "—", to: "/workspaces" },
        { k: "Findings", v: stats?.findings ?? "—", to: "/findings" },
        { k: "Roles", v: roleStats?.total_roles ?? "—", to: "/roles" },
        { k: "SoD Conflicts", v: roleStats?.total_conflicts ?? "—", to: "/roles" },
    ];

    return (
        <div className="panel-elevated relative overflow-hidden" data-testid={TID.dashboardHeroBanner}>
            {/* sentinel artwork, pushed to the right so it never fights the copy */}
            <img
                src="/assets/sentinel/hud-landscape.webp"
                alt=""
                aria-hidden="true"
                data-testid="dashboard-hero-art"
                className="absolute inset-y-0 right-0 h-full w-[62%] object-cover object-[35%_center] opacity-[0.20] theme-photo pointer-events-none"
            />
            <div className="absolute inset-0 pointer-events-none" style={{
                background: "linear-gradient(90deg, rgba(7,7,10,0.97) 0%, rgba(7,7,10,0.86) 42%, rgba(7,7,10,0.42) 100%)",
            }} />
            {/* cosmic canvas */}
            <div className="absolute inset-0 pointer-events-none" style={{
                background: "radial-gradient(ellipse 70% 90% at 50% 8%, rgb(var(--accent-2-rgb) / 0.20), transparent 62%), radial-gradient(circle at 82% 78%, rgb(var(--accent-1-rgb) / 0.10), transparent 55%)",
            }} />
            <div className="absolute inset-0 grid-bg opacity-[0.12] pointer-events-none" />
            <div className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgb(var(--accent-1-rgb) / 0.65), transparent)" }} />

            <div className="relative px-7 pt-8 pb-6">
                <div className="flex items-start justify-between gap-8 flex-wrap">
                    {/* portrait + motif */}
                    <div className="flex items-center gap-6 min-w-0">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="relative shrink-0"
                        >
                            <div className="absolute -inset-3 rounded-full border border-cyan-500/30" />
                            <div className="absolute -inset-6 rounded-full border border-cyan-500/12 animate-spin-slow" style={{ borderStyle: "dashed" }} />
                            <HologramHoverReveal />
                        </motion.div>
                        <div className="min-w-0">
                            <div className="label-eyebrow mb-1.5 flex items-center gap-1.5">
                                <Sparkles size={11} strokeWidth={1.5} /> Vision Beyond Limits
                            </div>
                            <h1 className="font-display text-3xl lg:text-4xl font-light text-white tracking-tight leading-none">
                                Executive Overview
                            </h1>
                            <p className="text-sm text-slate-400 mt-2 max-w-xl">
                                Engineering posture across every offline SAP artifact, role and control — one command centre.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col items-end gap-4">
                        <ShowreelLauncher compact />
                        <div className="flex items-center gap-2.5 flex-wrap justify-end">
                            {counters.map((c) => (
                                <button
                                    key={c.k}
                                    data-testid={TID.heroCounter(c.k.toLowerCase().replace(/\s+/g, "-"))}
                                    onClick={() => nav(c.to)}
                                    title={`Open ${c.k}`}
                                    className="hud-glass chrome-bevel px-4 py-2.5 rounded-sm text-center min-w-[96px] cursor-pointer hover:border-cyan-400/40 transition-colors"
                                >
                                    <div className="font-display text-xl text-cyan-300 leading-none">{c.v}</div>
                                    <div className="font-mono text-[9px] uppercase tracking-wider text-slate-400 mt-1.5">{c.k}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Command Center */}
                <div className="mt-7">
                    <div className="label-eyebrow mb-2">Command Center</div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="hud-glass chrome-bevel flex items-center gap-3 px-4 py-3 rounded-sm flex-1 min-w-[280px]">
                            <AudioLines size={16} className="text-cyan-400 shrink-0" strokeWidth={1.5} />
                            <input
                                data-testid={TID.heroCommandInput}
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && ask()}
                                placeholder="Ask Utsav anything about your landscape…"
                                className="bg-transparent border-0 outline-none text-sm text-slate-200 placeholder:text-slate-500 w-full"
                            />
                            <button
                                data-testid={TID.heroCommandSubmit}
                                onClick={() => ask()}
                                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 font-mono text-[10px] uppercase tracking-wider transition-colors"
                            >
                                Ask <ArrowRight size={11} />
                            </button>
                        </div>
                        {QUICK_PROMPTS.map((q) => (
                            <button
                                key={q}
                                data-testid={TID.heroQuickPrompt(q.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, "-"))}
                                onClick={() => ask(q)}
                                className="px-3 py-2 rounded-full border border-white/10 bg-black/30 text-[11px] text-slate-300 hover:text-cyan-200 hover:border-cyan-500/40 transition-colors"
                            >
                                {q}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
